import { useEffect } from 'react';
import InnerPageHero from '../components/InnerPageHero';

const sections = [
  {
    title: 'Information We Collect',
    content: [
      'We collect information you provide directly to us, such as when you request a service estimate, schedule an appointment, sign up for a membership plan, contact us with questions or feedback, or otherwise communicate with us.',
      'The types of information we may collect include your name, email address, phone number, mailing address, service address, payment information, and any other information you choose to provide.',
      'When you opt in to receive SMS/text messages from us, we also collect your mobile phone number and your consent to receive text communications. This information is collected solely for the purpose of sending you the communications you have requested.',
      'We also automatically collect certain information when you visit our website, including your IP address, browser type, operating system, referring URLs, pages viewed, and the dates and times of your visits.',
    ],
  },
  {
    title: 'How We Use Your Information',
    content: [
      'We use the information we collect to provide, maintain, and improve our services, including to process transactions, send service confirmations, and respond to your requests and inquiries.',
      'We may also use your information to send you marketing communications about our services, promotions, and membership plans. You may opt out of these communications at any time by contacting us or using the unsubscribe mechanism in our emails.',
      'Additionally, we use your information to monitor and analyze trends and usage, detect and prevent fraud, and comply with legal obligations.',
    ],
  },
  {
    title: 'SMS / Text Messaging',
    content: [
      'Opt-In: By providing your mobile phone number and affirmatively consenting on our website forms or other opt-in points, you agree to receive SMS/text messages from Best Neighbor Home Services. Consent to receive SMS messages is not a condition of purchasing any service.',
      'Message Types: We may send you appointment reminders, service updates, estimates, promotional offers, and other information related to your HVAC and home service needs.',
      'Message Frequency: Message frequency varies based on your interactions with us and the services you request.',
      'Message & Data Rates: Message and data rates may apply. Please check with your mobile carrier for details.',
      'Opt-Out: You may opt out of SMS communications at any time by replying STOP to any text message you receive from us. After opting out, you will receive one final confirmation message and no further SMS messages will be sent.',
      'Help: For assistance, reply HELP to any of our text messages or contact us directly at:',
      '• Phone: (903) 321-2265\n• Email: help@bestneighborhomeservices.com',
      'No Sharing of SMS Consent: Your mobile phone number and SMS opt-in consent will not be sold, shared, or transferred to third parties or affiliates for their own marketing purposes.',
    ],
  },
  {
    title: 'Information Sharing',
    content: [
      'We do not sell, trade, or otherwise transfer your personal information to outside parties except as described in this policy.',
      'We may share your information with trusted third-party service providers who assist us in operating our website, conducting our business, or servicing you, as long as those parties agree to keep this information confidential.',
      'We will never sell or share your SMS opt-in data or consent with third parties for marketing or promotional purposes.',
      'We may also release your information when we believe release is appropriate to comply with the law, enforce our site policies, or protect ours or others\' rights, property, or safety.',
    ],
  },
  {
    title: 'Data Security',
    content: [
      'We implement a variety of security measures to maintain the safety of your personal information. Your personal information is contained behind secured networks and is only accessible by a limited number of persons who have special access rights to such systems.',
      'All sensitive information you supply is transmitted via Secure Socket Layer (SSL) technology and then encrypted into our payment gateway providers\' databases, only to be accessible by those authorized with special access rights.',
    ],
  },
  {
    title: 'Cookies and Tracking',
    content: [
      'Our website may use cookies and similar tracking technologies to enhance your experience, analyze site usage, and assist in our marketing efforts.',
      'You can choose to disable cookies through your browser settings, though this may affect certain features of our website.',
    ],
  },
  {
    title: 'Third-Party Links',
    content: [
      'Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.',
    ],
  },
  {
    title: 'Children\'s Privacy',
    content: [
      'Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal information, we will take steps to delete such information.',
    ],
  },
  {
    title: 'Your Rights',
    content: [
      'You have the right to access, correct, or delete your personal information. You may also request that we restrict the processing of your data or object to certain uses.',
      'To exercise any of these rights, please contact us using the information provided below. We will respond to your request within a reasonable timeframe.',
    ],
  },
  {
    title: 'Changes to This Policy',
    content: [
      'We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically to stay informed about how we are protecting your information.',
    ],
  },
  {
    title: 'Contact Us',
    content: [
      'If you have any questions about this Privacy Policy or our SMS communications, please contact us at:',
      'Best Neighbor Home Services\n414 Metro Park Dr Suite 201\nMcKinney, TX 75071\nPhone: (903) 321-2265\nEmail: help@bestneighborhomeservices.com',
    ],
  },
];

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main id="main-content" tabIndex={-1}>
      <InnerPageHero
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Privacy Policy' },
        ]}
        title="Privacy"
        titleAccent="Policy"
        description="Your privacy is important to us. This policy outlines how Best Neighbor Home Services collects, uses, and protects your personal information."
      />

      <section className="bg-white py-16 sm:py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-brand-warm-gray mb-10">
            Last Updated: October 7, 2026
          </p>

          <div className="space-y-10">
            {sections.map((section, index) => (
              <div key={index}>
                <h2 className="text-xl sm:text-2xl font-serif font-bold text-brand-navy mb-4">
                  {index + 1}. {section.title}
                </h2>
                <div className="space-y-3">
                  {section.content.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-brand-warm-gray leading-relaxed text-[15px] whitespace-pre-line"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
